import { debounce, isMobile, domBatcher, prefersReducedMotion, checkIsAboveFold } from "./utilities-DtVOIXIY.js";
import { p as prepareEmblaInteraction, H as HybridCarousel, d as debouncedWatchResize } from "./embla-extension-rhthUxzc.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
import { unregisterSentryFlush, registerSentryFlush } from "./anim-batch-nQ-NAxq8.js";
import { getListingRevealController, scheduleDesignModeListingInit, cacheListingEmblaNodes, parseLiquidJSON, setupListingEmblaFeatures, enableListingNavigation, enableListingDots, flattenListingLayout, applyListingGridLayout, destroyListingEmbla, canSetupListingReveal, ensureListingRevealReadyForPlay } from "./listing-component-shared-xSf0ADsu.js";
import { w as waapi } from "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./native-scroll-snap-Cd15iSfl.js";
class IconBoxListing extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtns: [],
      nextBtns: [],
      dots: null
    };
    this.config = {
      options: {},
      autoPlay: {
        desktop: false,
        mobile: false
      }
    };
    this.gridConfig = {
      desktop: { rows: 1, itemsPerRow: 4 },
      mobile: { rows: 1, itemsPerRow: 1 }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
  }
  isInteractionReady() {
    var _a;
    return !!this.emblaApi || !((_a = this.nodes) == null ? void 0 : _a.viewport);
  }
  prepareInteraction() {
    if (Shopify.designMode && !this.isInteractionReady()) {
      this.init();
      return this.isInteractionReady();
    }
    return prepareEmblaInteraction(this);
  }
  connectedCallback() {
    var _a;
    this.cacheNodes();
    this.ssrRows = Number(this.dataset.ssrRows) || 1;
    this._domRows = this.ssrRows;
    this.gridConfig = {
      desktop: {
        rows: Number(this.dataset.rowsDesktop) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowDesktop) || 4
      },
      mobile: {
        rows: Number(this.dataset.rowsMobile) || 1,
        itemsPerRow: Number(this.dataset.itemsPerRowMobile) || 1
      }
    };
    const container = this.nodes.container;
    this.originalItems = Array.from(
      container ? container.querySelectorAll(":scope > .embla__slide > .icon-box") : []
    );
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    const controller = getListingRevealController(this);
    this._controller = controller;
    if (!controller && !Shopify.designMode) {
      this._initStandalone();
    } else if (controller) {
      const isDesktopActive = controller.classList.contains("active");
      const isMobileActive = (_a = controller.closest(".megamenu-mobile-container")) == null ? void 0 : _a.classList.contains("is-megamenu-active");
      if (controller.tagName.toLowerCase() !== "slideshow-component" && (isDesktopActive || isMobileActive)) {
        if (controller.classList.contains("megamenu-v2")) {
          const token = this._tabSetupToken;
          domBatcher.write(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        } else {
          const token = this._tabSetupToken;
          requestAnimationFrame(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.reset) == null ? void 0 : _a2.call(this);
          });
        }
      } else if ([
        "custom-dialog",
        "cart-drawer",
        "quick-view-dialog",
        "custom-div-popup"
      ].includes(controller.tagName.toLowerCase())) {
        const dialog = controller.querySelector("dialog");
        if (dialog && dialog.open) {
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
        const openHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.play) == null ? void 0 : _a2.call(this);
        });
        const closeHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.reset) == null ? void 0 : _a2.call(this);
        });
        controller.addEventListener("dialog:open", openHandler);
        controller.addEventListener("dialog:closing", closeHandler);
        if (typeof this.registerCleanup === "function") {
          this.registerCleanup(() => {
            controller.removeEventListener("dialog:open", openHandler);
            controller.removeEventListener("dialog:closing", closeHandler);
          });
        }
      }
    }
    if (Shopify.designMode) {
      scheduleDesignModeListingInit(this);
    }
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    var _a, _b, _c;
    window.removeEventListener("resize", this.handleResize);
    (_a = this._foldIO) == null ? void 0 : _a.disconnect();
    (_b = this._scrollObs) == null ? void 0 : _b.disconnect();
    if (this._introTrigger) {
      document.removeEventListener("theme:intro:complete", this._introTrigger);
      this._introTrigger = null;
    }
    if (this._sentryCallback) {
      unregisterSentryFlush(this._sentryCallback);
      this._sentryCallback = null;
    }
    (_c = this.reset) == null ? void 0 : _c.call(this);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    cacheListingEmblaNodes(this);
  }
  parseConfig() {
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoPlayDesktop = this.parseJSON(this.dataset.autoPlayDesktop, false);
    const autoPlayMobile = this.parseJSON(this.dataset.autoPlayMobile, false);
    this.config.autoPlay.desktop = autoPlayDesktop || false;
    this.config.autoPlay.mobile = autoPlayMobile || autoPlayDesktop;
  }
  parseJSON(jsonString, fallback) {
    return parseLiquidJSON(jsonString, fallback, "IconBoxListing");
  }
  getCurrentAutoPlayConfig() {
    return isMobile() ? this.config.autoPlay.mobile : this.config.autoPlay.desktop;
  }
  getCurrentGridConfig() {
    return isMobile() ? this.gridConfig.mobile : this.gridConfig.desktop;
  }
  // ========== Initialization ==========
  init() {
    this.destroy();
    const currentRows = this.getCurrentGridConfig().rows;
    if (this._domRows !== currentRows) {
      currentRows > 1 ? this.applyGridLayout() : this.flattenLayout();
      this._domRows = currentRows;
    }
    this.initEmbla();
    this._isFirstInit = false;
  }
  initEmbla() {
    if (Shopify.designMode) {
      this._doInitEmbla();
    } else {
      if (this._emblaInitQueued) return;
      this._emblaInitQueued = true;
      if (this._ioInit) this._ioInit.disconnect();
      this._ioInit = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this._ioInit.disconnect();
            this._emblaInitQueued = false;
            if (!this.isConnected) return;
            this._doInitEmbla();
          }
        },
        { rootMargin: "200px" }
      );
      this._ioInit.observe(this);
    }
  }
  _doInitEmbla() {
    if (!this.nodes.viewport) return;
    const autoPlayConfig = this.getCurrentAutoPlayConfig();
    const plugins = [];
    if (autoPlayConfig && !prefersReducedMotion()) {
      plugins.push(
        Autoplay({
          delay: autoPlayConfig.delay ?? 4e3,
          stopOnInteraction: autoPlayConfig.stopOnInteraction ?? false,
          stopOnMouseEnter: autoPlayConfig.stopOnMouseEnter ?? true,
          stopOnFocusIn: false
        })
      );
    }
    try {
      this.emblaApi = HybridCarousel(
        this.nodes.viewport,
        { ...this.config.options, watchResize: debouncedWatchResize() },
        plugins
      );
      this.featureCleanups = [];
      this.setupEmblaFeatures();
      this.emblaApi.on("reInit", () => this.setupEmblaFeatures());
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    setupListingEmblaFeatures(this);
  }
  enableNavigation() {
    enableListingNavigation(this);
  }
  enableDots() {
    enableListingDots(this);
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    var _a;
    if (!this.isInteractionReady()) this.prepareInteraction();
    (_a = this.emblaApi) == null ? void 0 : _a.scrollTo(slideIndex);
  }
  // ========== Grid Layout (mobile only — desktop is SSR) ==========
  flattenLayout() {
    flattenListingLayout(this);
  }
  applyGridLayout() {
    applyListingGridLayout(this);
  }
  // ========== Resize ==========
  onResize() {
    const newDevice = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDevice) {
      this.currentDeviceType = newDevice;
      this.init();
    }
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    if (this._ioInit) {
      this._ioInit.disconnect();
      this._ioInit = null;
    }
    destroyListingEmbla(this);
  }
  // ========== Tab Group Animation API ==========
  _initStandalone() {
    if (prefersReducedMotion() || isMobileAnim || document.body.dataset.animationOnScroll !== "true") {
      if (typeof this.init === "function") this.init();
      return;
    }
    this._foldIO = new IntersectionObserver(
      (entries) => {
        this._foldIO.disconnect();
        this._foldIO = null;
        requestAnimationFrame(() => {
          const isAboveFold = checkIsAboveFold(entries[0]);
          if (isAboveFold) {
            if (typeof this.init === "function") this.init();
            return;
          }
          if (!this.setup()) {
            if (this._isFirstInit && typeof this.init === "function")
              this.init();
            return;
          }
          this._prewarmObs = new IntersectionObserver(
            (entries2) => {
              if (entries2[0].isIntersecting) {
                this._prewarmObs.disconnect();
                this._prewarmObs = null;
                if (this._isFirstInit && typeof this.init === "function")
                  this.init();
              }
            },
            { rootMargin: "0px 0px 50% 0px" }
          );
          this._prewarmObs.observe(this);
          this._scrollObs = new IntersectionObserver(
            (entries2) => {
              if (entries2[0].isIntersecting) {
                this._scrollObs.disconnect();
                this._scrollObs = null;
                this.play();
              }
            },
            { rootMargin: "0px 0px -10% 0px" }
          );
          this._scrollObs.observe(this);
          this._sentryCallback = () => {
            const obs = this._scrollObs || this._animObserver;
            if (obs) {
              const checkObs = new IntersectionObserver(
                (checkEntries) => {
                  checkObs.disconnect();
                  if (checkEntries[0].isIntersecting) {
                    const currentObs = this._scrollObs || this._animObserver;
                    if (currentObs) {
                      currentObs.disconnect();
                      if (this._scrollObs) this._scrollObs = null;
                      if (this._animObserver) this._animObserver = null;
                      if (typeof this.play === "function") this.play();
                    }
                  }
                },
                { threshold: 0 }
              );
              checkObs.observe(this);
            }
          };
          registerSentryFlush(this._sentryCallback);
        });
      },
      { threshold: 0 }
    );
    this._foldIO.observe(this);
  }
  setup() {
    if (prefersReducedMotion() || isMobileAnim) return false;
    if (this._ready) return true;
    if (!canSetupListingReveal(this)) return false;
    const items = Array.from(this.querySelectorAll(".icon-box"));
    items.forEach((item) => {
      item.style.willChange = "transform, opacity";
      item.style.opacity = "0";
      item.style.transform = isMobileAnim ? "translateY(10px)" : "translateY(30px)";
    });
    this._animItems = items;
    this._ready = true;
    return true;
  }
  play(delayMs = 0) {
    var _a;
    const wasFirstInit = this._isFirstInit;
    if (this.emblaApi && !wasFirstInit) {
      requestAnimationFrame(() => {
        if (this.emblaApi) this.emblaApi.reInit();
      });
    }
    if (prefersReducedMotion() || isMobileAnim) {
      if (this._isFirstInit) {
        if (document.readyState === "complete") {
          if (typeof window.requestIdleCallback === "function") {
            requestIdleCallback(() => {
              if (this.isConnected) this.init();
            });
          } else {
            setTimeout(() => {
              if (this.isConnected) this.init();
            }, 100);
          }
        } else {
          window.addEventListener(
            "load",
            () => {
              if (typeof window.requestIdleCallback === "function") {
                requestIdleCallback(() => {
                  if (this.isConnected) this.init();
                });
              } else {
                setTimeout(() => {
                  if (this.isConnected) this.init();
                }, 100);
              }
            },
            { once: true }
          );
        }
      }
      return;
    }
    if (!ensureListingRevealReadyForPlay(this)) {
      if (this._isFirstInit && typeof this.init === "function") this.init();
      return;
    }
    const items = Array.from(this.querySelectorAll(".icon-box"));
    if (items.length === 0) return;
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    const baseDelay = delayMs || 0;
    const staggerAmount = isMobileAnim ? 30 : 60;
    const duration = isMobileAnim ? 300 : 600;
    const easing = "cubic-bezier(0.16, 1, 0.3, 1)";
    const maxDuration = duration + baseDelay + items.length * staggerAmount;
    clearTimeout(this._failsafeTimer);
    this._failsafeTimer = setTimeout(() => this.reset(), maxDuration + 200);
    try {
      this._anim = waapi.animate(items, {
        opacity: [
          0,
          1
        ],
        transform: [
          isMobileAnim ? "translateY(10px)" : "translateY(30px)",
          "translateY(0px)"
        ],
        duration,
        delay: (_, i) => baseDelay + i * staggerAmount,
        ease: easing,
        composition: "replace",
        onComplete: () => {
          clearTimeout(this._failsafeTimer);
          this._anim = null;
          items.forEach((item) => {
            item.style.removeProperty("opacity");
            item.style.removeProperty("transform");
            item.style.removeProperty("will-change");
          });
          if (this._isFirstInit) {
            if (document.readyState === "complete") {
              if (typeof window.requestIdleCallback === "function") {
                requestIdleCallback(() => {
                  if (this.isConnected) this.init();
                });
              } else {
                setTimeout(() => {
                  if (this.isConnected) this.init();
                }, 100);
              }
            } else {
              window.addEventListener(
                "load",
                () => {
                  if (typeof window.requestIdleCallback === "function") {
                    requestIdleCallback(() => {
                      if (this.isConnected) this.init();
                    });
                  } else {
                    setTimeout(() => {
                      if (this.isConnected) this.init();
                    }, 100);
                  }
                },
                { once: true }
              );
            }
          }
        }
      });
    } catch (err) {
      this.reset();
    }
    this._animItems = items;
  }
  reset() {
    var _a;
    if (prefersReducedMotion() || isMobileAnim) {
      if (this._isFirstInit) {
        if (document.readyState === "complete") {
          if (typeof window.requestIdleCallback === "function") {
            requestIdleCallback(() => {
              if (this.isConnected) this.init();
            });
          } else {
            setTimeout(() => {
              if (this.isConnected) this.init();
            }, 100);
          }
        } else {
          window.addEventListener(
            "load",
            () => {
              if (typeof window.requestIdleCallback === "function") {
                requestIdleCallback(() => {
                  if (this.isConnected) this.init();
                });
              } else {
                setTimeout(() => {
                  if (this.isConnected) this.init();
                }, 100);
              }
            },
            { once: true }
          );
        }
      }
      return;
    }
    clearTimeout(this._failsafeTimer);
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    this._anim = null;
    const items = this._animItems || Array.from(this.querySelectorAll(".icon-box"));
    items.forEach((item) => {
      item.style.removeProperty("opacity");
      item.style.removeProperty("transform");
      item.style.removeProperty("will-change");
    });
    if (this._isFirstInit) {
      if (document.readyState === "complete") {
        if (typeof window.requestIdleCallback === "function") {
          requestIdleCallback(() => {
            if (this.isConnected) this.init();
          });
        } else {
          setTimeout(() => {
            if (this.isConnected) this.init();
          }, 100);
        }
      } else {
        window.addEventListener(
          "load",
          () => {
            if (typeof window.requestIdleCallback === "function") {
              requestIdleCallback(() => {
                if (this.isConnected) this.init();
              });
            } else {
              setTimeout(() => {
                if (this.isConnected) this.init();
              }, 100);
            }
          },
          { once: true }
        );
      }
    }
    this._ready = false;
  }
}
if (!customElements.get("icon-box-listing-component")) {
  customElements.define("icon-box-listing-component", IconBoxListing);
}
